/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Container, Box, CircularProgress, Grid } from '@mui/material';
import { useAuth } from './auth/AuthProvider';

import { Dashboard, ElasticityPanel, SkuDashboard, Header, LpoEntryRedirect } from './components';
import {
  LPOFormLayout, ReviewSuppliers, OtpLogin,
  LPOEntryModeSelector, LPOFormManual, LPOFormVoice,LandingPage,
  VoiceSubmission, MyInsightsPage, BillingPage, PublicSignUp,
  SignupSuccess, SignupCancelled, SignupVerify,
  ReviewInsight, SecureGateLookup, NetSuitePOSearch,
  BOMSearch, AuthCallback, RfqBundlingPage, DecisionArtifacts, ConfirmAction, CapturePage, DemoPage,
  DemoStory, AuditDashboard, MyIncidents, IncidentWorkspaceDetail, ReviewQueue, OperationsCenter,
  AutomateCapturePage, MyWorkItems, ActivationPage, TrialWelcome, OnboardingInvites,
  TeamActivationPage, TeamAdministration
} from './pages';
import Button from '@mui/material/Button';
import AdminConsole from './pages/AdminConsole'; 
import FounderMemory from './pages/FounderMemory';
import OperationalMemory from './pages/OperationalMemory';
import TechnicianAssist from './pages/TechnicianAssist';
import AdminSidebar from './components/AdminSidebar';
import LPOEntryRedirect from './components/LpoEntryRedirect';

import { PublicClientApplication } from "@azure/msal-browser";

export const msal = new PublicClientApplication({
  auth: {
    clientId: "8fcde660-f8cd-4f26-b3e4-94d804be561f",
    authority: "https://login.microsoftonline.com/organizations",
    redirectUri:
      process.env.NODE_ENV === "production"
        ? "https://app.fluxonix.io/auth/callback"
        : window.location.origin,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
});

/* --------------------------------------------------------------------------
   NEW: AppInit wrapper — blocks rendering until MSAL redirect is processed
-------------------------------------------------------------------------- */
function AppInit({ children }) {
  const [msalReady, setMsalReady] = useState(false);

  useEffect(() => {
    let active = true;

    async function initMsal() {
      try {
        const resp = await msal.handleRedirectPromise();

        if (resp?.account) {
          console.log("✅ Redirect handled:", resp.account.username);
          msal.setActiveAccount(resp.account);
        } else {
          const account = msal.getAllAccounts()[0];
          if (account) msal.setActiveAccount(account);
        }

        window._fluxonix_msal = msal;
      } catch (e) {
        console.error("MSAL redirect error:", e);
      }

      if (active) setMsalReady(true);
    }

    initMsal();
    return () => { active = false; };
  }, []);

  if (!msalReady) {
    return (
      <Box display="flex" justifyContent="center" height="100vh" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  return children;
}

// const authDisabled = process.env.REACT_APP_AUTH_DISABLED === "true";

function LoginRequiredRedirect() {
  const location = useLocation();
  const intendedPath = location.pathname + location.search + location.hash;
  sessionStorage.setItem("fluxonix_post_login_path", intendedPath);
  return <Navigate to="/" replace />;
}

function UnauthLayout() {
  const location = useLocation();
  const isActivation = ["/activate", "/join"].includes(location.pathname);
  return (
    <>
      {!isActivation && <Header isAuthDisabled />}
      <Container maxWidth={isActivation ? false : "lg"} disableGutters={isActivation}>
        <Box sx={{ py: isActivation ? 0 : 3 }}>
          <Routes>
            {/* <Route path="/" element={<LandingPage />} /> */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/bom-lookup" element={<BOMSearch />} />
            <Route path="/rfq-demo" element={<RfqBundlingPage />} />
            <Route path="/signup/verify" element={<SignupVerify />} />
            <Route path="/signup/cancelled" element={<SignupCancelled />} />
            <Route path="/signup" element={<PublicSignUp />} />
            <Route path="/activate" element={<ActivationPage />} />
            <Route path="/join" element={<TeamActivationPage />} />
            <Route path="/auth/callback" element={<Navigate to="/" replace />} />
            <Route path="/secure/*" element={<LoginRequiredRedirect />} />
            <Route path="*" element={<div>404 - Page not found</div>} />
            <Route path="/otp-login" element={<OtpLogin />} />
            <Route path="/lpo-form" element={<LPOFormLayout />} />
            <Route path="/lpo-entry-mode" element={<LPOEntryModeSelector />} />
            <Route path="/lpo-form/manual" element={<LPOFormManual />} />
            <Route path="/lpo-form/voice" element={<LPOFormVoice />} />
            <Route path="/secure/voice" element={<VoiceSubmission />} />
            <Route path="/secure/my-insights" element={<MyInsightsPage />} />
            <Route path="/capture/:slug" element={<CapturePage />} />
            <Route path="/demo/audit" element={<DemoPage />} />
            <Route path="demo-story" element={<DemoStory />} />
            <Route path="audit-dashboard" element={<AuditDashboard />} />
            <Route path="/capture/automate/:slug" element={<AutomateCapturePage />} />
            <Route path="/operational-memory" element={<OperationalMemory />} />
            <Route path="/operational-memory/ask" element={<OperationalMemory />} />
            <Route path="/operational-memory/records/:recordId" element={<OperationalMemory />} />
          </Routes>
        </Box>
      </Container>
    </>
  );
}


function AuthenticatedApp() {
  const { user, isLoading, isAuthenticated, login, logout, roles } = useAuth(); // ✅ ensure roles is returned
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/");
    }
  }, [isLoading, user, navigate]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null; // wait for redirect
  }

  return (
    <>
      <Header
        showLogin={!isAuthenticated}
        onLogin={login}
        onLogout={isAuthenticated ? logout : null}
      />

      <Container maxWidth={false} disableGutters>
        <Grid container spacing={2}>
          {/* ✅ Conditionally render AdminSidebar if user has admin role */}
          {/* {roles?.includes("admin") && (
            <Grid item xs={12} md={3}>
              <AdminSidebar />
            </Grid>
          )} */}

          <Grid item xs={12} md={roles?.includes("admin") ? 9 : 12}>
            <Box sx={{ py: 3 }}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/admin-ui" element={<Dashboard />} />
                <Route path="/otp-login" element={<OtpLogin />} />
                <Route path="/lpo-form" element={<LPOEntryModeSelector />} />
                <Route path="/lpo-entry-mode" element={<LpoEntryRedirect />} />
                <Route path="/lpo-form/manual" element={<LPOFormManual />} />
                <Route path="/lpo-form/voice" element={<LPOFormVoice />} />
                <Route path="/admin" element={<AdminConsole />} />
                <Route path="/review-suppliers" element={<ReviewSuppliers />} />
                <Route path="/admin/settings" element={<Navigate to="/admin/billing" replace />} />
                <Route path="/admin/connectors" element={<Navigate to="/admin/billing" replace />} />
                <Route path="/admin/connector-config" element={<Navigate to="/admin/billing" replace />} />
                <Route path="/secure/voice" element={<VoiceSubmission />} />
                <Route path="/secure/my-insights" element={<MyInsightsPage />} />
                <Route path="*" element={<div>404 - Page not found</div>} />
                <Route path="/unauthorized" element={<div>🚫 You do not have access to this page.</div>} />
                <Route path="/admin/billing" element={<BillingPage />} />
                <Route path="/secure/lookup" element={<SecureGateLookup />} />
                <Route path="/signup/success" element={<SignupSuccess />} />
                <Route path="/signup/cancelled" element={<SignupCancelled />} />
                <Route path="/secure/review-insights/:id" element={<ReviewInsight />} />
                <Route path="/secure/netsuite/po/search" element={<NetSuitePOSearch />} />
                <Route path="/admin/artifacts" element={<DecisionArtifacts />} />
                <Route path="/confirm-action/:submissionId" element={<ConfirmAction />} />
                <Route path="/secure/my-incidents" element={<MyIncidents />} />
                <Route path="/secure/assist" element={<TechnicianAssist />} />
                <Route path="/secure/founder-memory" element={<FounderMemory />} />
                <Route path="/operational-memory" element={<OperationalMemory />} />
                <Route path="/operational-memory/ask" element={<OperationalMemory />} />
                <Route path="/operational-memory/records/:recordId" element={<OperationalMemory />} />
                <Route path="/secure/incidents/:incidentId" element={<IncidentWorkspaceDetail />} />
                <Route path="/secure/reviews" element={<ReviewQueue />} />
                <Route path="/secure/operations-center" element={<OperationsCenter />} />
                <Route path="/secure/my-work-items" element={<MyWorkItems />}/>
                <Route path="/onboarding/welcome" element={<TrialWelcome />} />
                <Route path="/admin/onboarding/invitations" element={<OnboardingInvites />} />
                <Route path="/admin/team" element={<TeamAdministration />} />
              </Routes>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

function App() {
  const { user } = useAuth();

  return (
    <AppInit>
      <Router>
        {!user ? <UnauthLayout /> : <AuthenticatedApp />}
      </Router>
    </AppInit>
  );
}

export default App;
